import type { JSX } from 'react';

/**
 * FASE 197 (201.9) — icones SVG inline proprios (lucide-style: stroke currentColor, 24x24 viewBox,
 * stroke-width 2, round caps) no lugar de emoji-como-icone (anti-pattern ANTI AI-SLOP item 11: `⚡`/`⏳`/`✓`
 * variavam por plataforma/fonte e destoavam da identidade Ayu Mirage). Tamanho 1em acompanha o font-size do
 * contexto; `aria-hidden` porque os textos adjacentes ja carregam o significado (os sites mantem aria-label
 * proprio quando preciso). POLISH, nao redesign — o design system segue o mesmo.
 */
function svgProps(): JSX.IntrinsicElements['svg'] {
  return {
    width: '1em',
    height: '1em',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    focusable: false,
    style: { verticalAlign: '-0.125em' },
  };
}

/** Raio/effort (substitui `⚡`). */
export function IconBolt(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-bolt">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

/** Ampulheta/aguardando (substitui `⏳`). */
export function IconHourglass(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-hourglass">
      <path d="M5 22h14" />
      <path d="M5 2h14" />
      <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
      <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
    </svg>
  );
}

/** Check simples (substitui `✓`). */
export function IconCheck(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-check">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/** Check duplo (substitui `✓✓` — aceitar recomendacao do revisor). */
export function IconCheckDouble(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-check-double">
      <path d="M18 6 7 17l-5-5" />
      <path d="m22 10-7.5 7.5L13 16" />
    </svg>
  );
}

export function IconChevronDown(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-chevron-down">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function IconChevronRight(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-chevron-right">
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

export function IconMicrophone(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-microphone">
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <path d="M12 17v5" />
      <path d="M8 22h8" />
    </svg>
  );
}

export function IconStop(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-stop">
      <rect x="6" y="6" width="12" height="12" rx="1" />
    </svg>
  );
}

export function IconLoader(): JSX.Element {
  return (
    <svg {...svgProps()} className="icon-loader" data-testid="icon-loader">
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
    </svg>
  );
}

export function IconClose(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-close">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

export function IconEdit(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-edit">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

export function IconClock(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-clock">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

export function IconHome(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-home">
      <path d="m3 11 9-8 9 8" />
      <path d="M5 10v10h14V10" />
      <path d="M9 20v-6h6v6" />
    </svg>
  );
}

export function IconProjects(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-projects">
      <path d="M3 7h7l2 2h9v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      <path d="M3 7V5a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

export function IconSessions(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-sessions">
      <rect x="3" y="4" width="18" height="13" rx="2" />
      <path d="m7 9 3 2-3 2" />
      <path d="M11 21h2" />
      <path d="M12 17v4" />
    </svg>
  );
}

export function IconTerminal(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-terminal">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="m7 9 3 3-3 3" />
      <path d="M13 15h4" />
    </svg>
  );
}

export function IconInbox(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-inbox">
      <path d="M4 4h16l1 12h-5l-2 3h-4l-2-3H3Z" />
      <path d="M3 16h5" />
      <path d="M16 16h5" />
    </svg>
  );
}

export function IconMore(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-more">
      <circle cx="5" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IconActivity(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-activity">
      <path d="M3 12h4l2-7 4 14 2-7h6" />
    </svg>
  );
}

export function IconSettings(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-settings">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1v.1h-4v-.1a1.7 1.7 0 0 0-1.1-1.6 1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1-.4h-.1v-4H3A1.7 1.7 0 0 0 4.6 8.5a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1v-.1h4V3A1.7 1.7 0 0 0 15.5 4.6a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 9c.16.36.37.7.6 1 .27.24.62.38 1 .4h.1v4H21a1.7 1.7 0 0 0-1.6.6Z" />
    </svg>
  );
}

export function IconPause(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-pause">
      <path d="M8 5v14" />
      <path d="M16 5v14" />
    </svg>
  );
}

export function IconPlay(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-play">
      <path d="m7 4 13 8-13 8Z" />
    </svg>
  );
}

export function IconRefresh(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-refresh">
      <path d="M20 11a8 8 0 0 0-14.8-4" />
      <path d="M4 4v5h5" />
      <path d="M4 13a8 8 0 0 0 14.8 4" />
      <path d="M20 20v-5h-5" />
    </svg>
  );
}

export function IconInfo(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-info">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </svg>
  );
}

export function IconArrowLeft(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-arrow-left">
      <path d="m15 18-6-6 6-6" />
      <path d="M21 12H9" />
    </svg>
  );
}

export function IconShieldCheck(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-shield-check">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

export function IconMessageSquare(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-message-square">
      <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />
    </svg>
  );
}

export function IconWarning(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-warning">
      <path d="M12 3 2.5 20h19Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

export function IconSearch(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-search">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-4-4" />
    </svg>
  );
}

export function IconArrowDown(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-arrow-down">
      <path d="M12 5v14" />
      <path d="m19 12-7 7-7-7" />
    </svg>
  );
}

export function IconImage(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-image">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9" r="1.5" />
      <path d="m21 15-5-5L5 20" />
    </svg>
  );
}

/** ECO.8-S — levar para fora (abrir a sessão numa janela do Windows Terminal). */
export function IconExternal(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-external">
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

/** ECO.18 — cadeado: "a gravação continua com a tela bloqueada" (app Android). */
export function IconLock(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-lock">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

/** ECO4.1-S — segundo cérebro: pessoa (origem dono). */
export function IconUser(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-user">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  );
}

/** Brilho (origem Assistente). */
export function IconSparkle(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-sparkle">
      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9Z" />
      <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8Z" />
    </svg>
  );
}

/** Camadas (pasta Áreas). */
export function IconLayers(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-layers">
      <path d="M12 3 2 8l10 5 10-5Z" />
      <path d="m2 13 10 5 10-5" />
    </svg>
  );
}

/** Livro (pasta Recursos). */
export function IconBook(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-book">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5Z" />
      <path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5" />
    </svg>
  );
}

/** Caixa de arquivo (pasta Arquivo / arquivar). */
export function IconArchive(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-archive">
      <rect x="2" y="3" width="20" height="5" rx="1" />
      <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
      <path d="M10 12h4" />
    </svg>
  );
}

/** Etiqueta. */
export function IconTag(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-tag">
      <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8Z" />
      <circle cx="7.5" cy="7.5" r="1.5" />
    </svg>
  );
}

/** Corrente (ligação). */
export function IconLink(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-link">
      <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
      <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
    </svg>
  );
}

/** Grafo (mapa de ligações). */
export function IconGraph(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-graph">
      <circle cx="5" cy="6" r="2.5" />
      <circle cx="19" cy="5" r="2.5" />
      <circle cx="12" cy="19" r="2.5" />
      <path d="M7.3 7.2 10.8 17" />
      <path d="m16.7 6.3-3.6 10.5" />
      <path d="M7.5 6h9" />
    </svg>
  );
}

/** Mover para pasta. */
export function IconFolderMove(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-folder-move">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      <path d="M9 13h6" />
      <path d="m13 11 2 2-2 2" />
    </svg>
  );
}

/** Copiar. */
export function IconCopy(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-copy">
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
    </svg>
  );
}

/** Mais (adicionar). */
export function IconPlus(): JSX.Element {
  return (
    <svg {...svgProps()} data-testid="icon-plus">
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}
